// You-pick / build-your-own "slot bundles": a parent product whose items the
// customer chooses one slot at a time in the slot wizard, stored in
// v3Order.productConfig.
import { selectConfiguredChildProducts } from './configFlow';

// The slice of Redux state this selector reads: the slot wizard's selections,
// keyed by slot index (an array in older persisted carts).
export interface SlotBundleState {
  v3Order?: { productConfig?: Record<string, unknown> | unknown[] };
}

export interface BundleProduct {
  is_ypx?: boolean;
  is_byo?: boolean;
  is_multi_config?: boolean;
  child_products?: unknown[];
  [key: string]: unknown;
}

// Multi-config parents are excluded: their children are fixed by the price
// list and never live in productConfig. Mirrors the server's
// OrderSubmit::CheckForUnconfiguredBundles.
export const isSlotBundleProduct = (
  product: BundleProduct | null | undefined,
): boolean =>
  !!product &&
  !product.is_multi_config &&
  (product.is_ypx === true || product.is_byo === true);

// A slot bundle the customer cannot complete, because the catalog links no
// child products to it. Mirrors CompanyPricingModelProduct#childless_slot_bundle?.
// The wizard renders an empty rail for one of these, so it is a broken price
// list rather than something the customer failed to do — worth saying so
// instead of asking them to choose items that do not exist.
// Deliberately requires child_products to be PRESENT and empty. A product
// payload that carries no child_products key at all says nothing about the
// price list — older persisted carts and other serializers omit it — and
// reading a missing key as "no children" would tell a customer a perfectly
// orderable package is unavailable. Absent means unknown; fall back to the
// generic "choose the items" path.
export const isChildlessSlotBundle = (
  product: BundleProduct | null | undefined,
): boolean =>
  isSlotBundleProduct(product) &&
  Array.isArray(product?.child_products) &&
  product.child_products.length === 0;

// Whether adding `product` now would put a slot bundle with no items chosen in
// the cart — the lab would get an empty package (ENG-1683). Items are the slot
// selections with a product picked, the same ones configureCartItem turns into
// the cart item's children.
export const selectIsUnconfiguredSlotBundle = (
  state: SlotBundleState,
  product: BundleProduct | null | undefined,
): boolean =>
  isSlotBundleProduct(product) &&
  selectConfiguredChildProducts(state).length === 0;
